<script setup lang="ts">
import { reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSession } from '../stores/session'
import { useForm } from '../lib/form'
import { LOCALES, type Locale } from '../i18n'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiField from '../components/UiField.vue'
import ErrorBanner from '../components/ErrorBanner.vue'

const route = useRoute()
const router = useRouter()
const { locale } = useI18n()
const session = useSession()
const { busy, error, submit, fieldErr } = useForm()
const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : '/'
const form = reactive({
  display_name: '',
  email: typeof route.query.email === 'string' ? route.query.email : '',
  password: '',
  locale: locale.value as Locale,
})

async function onSubmit() {
  if (await submit(() => session.register({ ...form }))) router.replace(redirect)
}
</script>

<template>
  <UiCard :title="$t('auth.registerTitle')" :subtitle="$t('auth.registerSubtitle')">
    <form class="stack" @submit.prevent="onSubmit">
      <ErrorBanner :error="error" />
      <UiField :label="$t('fields.displayName')" :error="fieldErr('display_name')">
        <input v-model.trim="form.display_name" name="display_name" autocomplete="name" required maxlength="100" :aria-invalid="!!fieldErr('display_name')" />
      </UiField>
      <UiField :label="$t('fields.email')" :error="fieldErr('email')">
        <input v-model.trim="form.email" name="email" type="email" autocomplete="email" required :aria-invalid="!!fieldErr('email')" />
      </UiField>
      <UiField :label="$t('fields.password')" :error="fieldErr('password')" :hint="$t('auth.passwordHint')">
        <input v-model="form.password" name="password" type="password" autocomplete="new-password" required minlength="10" :aria-invalid="!!fieldErr('password')" />
      </UiField>
      <UiField :label="$t('fields.locale')" :error="fieldErr('locale')">
        <select v-model="form.locale" name="locale">
          <option v-for="l in LOCALES" :key="l" :value="l">{{ $t(`locales.${l}`) }}</option>
        </select>
      </UiField>
      <UiButton type="submit" :loading="busy">{{ $t('auth.registerAction') }}</UiButton>
      <p class="small muted">
        {{ $t('auth.haveAccount') }}
        <RouterLink :to="{ path: '/login', query: route.query }">{{ $t('auth.loginLink') }}</RouterLink>
      </p>
    </form>
  </UiCard>
</template>
